/**
 * Centralised API client — all fetch calls go through here.
 * In development, Vite proxies /api → http://localhost:4000/api
 * In production, requests go to the same origin (frontend served by backend)
 *   OR to VITE_API_URL if frontend/backend are on different domains.
 */
/// <reference types="vite/client" />

const BASE = import.meta.env?.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL.trim()}/api`
  : '/api';

const TOKEN_KEY = 'auth_token';

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Dispatch a custom event so AuthContext can react to 401 globally.
 * We skip dispatching for /auth/me and user-specific endpoints that should handle 401 gracefully.
 */
function handle401(path: string) {
  // Skip automatic logout for user-specific endpoints that handle 401 gracefully
  const userEndpoints = [
    '/auth/me',
    '/dashboard/user/',
    '/videos/user',
    '/subscriptions/my',
    '/videos/user/',
    '/subscriptions/my/'
  ];

  const shouldSkipLogout = userEndpoints.some(endpoint => path.includes(endpoint));

  if (!shouldSkipLogout) {
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }
}

async function request<T = any>(
  method: Method,
  path: string,
  body?: unknown
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body) headers['Content-Type'] = 'application/json';
  const token = getStoredToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',         // send cookies cross-origin
    headers,
    body:    body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    if (res.status === 401) {
      handle401(path);
    }
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Request failed');
  }
  return res.json() as Promise<T>;
}

export const api = {
  get:    <T = any>(path: string)                    => request<T>('GET',    path),
  post:   <T = any>(path: string, body?: unknown)    => request<T>('POST',   path, body),
  put:    <T = any>(path: string, body?: unknown)    => request<T>('PUT',    path, body),
  patch:  <T = any>(path: string, body?: unknown)    => request<T>('PATCH',  path, body),
  delete: <T = any>(path: string)                    => request<T>('DELETE', path),
};
